import { useState, useEffect } from 'react'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import './Billing.css'

const Billing = () => {
  const [activeSection, setActiveSection] = useState('overview')

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'credits', title: 'Free Credits' },
    { id: 'payment-methods', title: 'Payment Methods' },
    { id: 'invoices', title: 'View Invoices' },
    { id: 'charges', title: 'Invoice Charges' },
    { id: 'stripe', title: 'Manage in Stripe' }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(`billing-${sections[i].id}`)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  return (
    <div className="billing-page">
      <div className="billing-content-wrapper">
        <div className="billing-main-content">
      <h1>Billing</h1>
      <p className="intro">
            Manage your billing, payment methods, and view invoices on Aedify.AI. Billing is processed 
            monthly per organization for resource usage.
      </p>

          <section id="billing-overview" className="billing-section">
            <h2>Overview</h2>
            <p>
              Billing for Aedify.AI is monthly per organization. Organizations are administrative entities 
              that enable you to add members, share app development environments, and manage billing.
            </p>
        <p>
              New customers and all new organizations (including those created by previously existing customers) 
              are billed monthly for resource usage. Refer to our <a href="https://aedify.ai/pricing" target="_blank" rel="noopener noreferrer" className="inline-link">Pricing page</a> for details.
        </p>
            <p>
              Aedify.AI uses a <strong>postpaid billing model</strong>. Usage is tracked throughout the month 
              and billed at the end of the billing cycle based on your actual resource consumption.
            </p>
          </section>

          <section id="billing-credits" className="billing-section">
            <h2>Free Credits</h2>
            <p>
              New users receive <strong>$50 in free credits</strong> upon signup to get started with Aedify.AI. 
              These credits can be used for inference, compute, and app runtime costs.
            </p>
            <p>
              Additionally, when you add a credit card to your account, you'll receive an extra <strong>$50 bonus credits</strong>. 
              This helps you explore the platform without immediate charges.
            </p>
            <p>
              View your current credit balance in the <strong>Billing</strong> section of your dashboard. Credits are 
              automatically applied to your usage costs and deducted as you use services.
            </p>
      </section>

          <section id="billing-payment-methods" className="billing-section">
        <h2>Payment Methods</h2>
        <p>
              Payments are processed through <strong>Stripe</strong>, a secure payment processor. You can add, update, or remove 
              payment methods from the Billing section of your dashboard.
            </p>
            <h3>How We Use Credit Cards</h3>
            <p>
              An <strong>active, valid credit card</strong> on file is required for most Aedify.AI accounts to perform actions like 
              deploying multiple apps and public images. The primary purpose is to <strong>prevent abuse</strong> and ensure payment 
              collection at the end of the month.
            </p>
            <h3>Pre-authorization Requests</h3>
            <p>
              Aedify.AI may send a <strong>pre-authorization request</strong> to your bank to verify authorization for charges. 
              Credit card companies might present these as "real charges," which can be surprising.
            </p>
            <p>
              A pre-authorization is a <strong>hold on a credit card</strong>, not a real charge. Typically, a small amount 
              (usually less than $5) is pre-authorized after signup, then immediately canceled. Banks may show the pre-authorization 
              for <strong>up to 7 days</strong>, even if no money has been collected.
        </p>
            <h3>Managing Payment Methods</h3>
            <p>
              From the Billing section, you can:
            </p>
            <ul>
              <li>Add new payment methods (credit cards)</li>
              <li>Set a default payment method</li>
              <li>Delete payment methods (you cannot delete your last payment method if you have an outstanding balance)</li>
              <li>View payment method details (card brand, last 4 digits, expiration date)</li>
            </ul>
            <p>
              All payment information is encrypted and processed securely by Stripe. We never store your full card information.
            </p>
      </section>

          <section id="billing-invoices" className="billing-section">
            <h2>View Invoices</h2>
        <p>
              You can view and download invoices from the <strong>Billing</strong> section of your <strong>dashboard</strong> for each organization.
            </p>
            <p>
              To download a past invoice, click <strong>View</strong> for the relevant billing cycle. You'll be sent to the Stripe billing 
              portal where you'll have the option to download the invoice as a PDF.
            </p>
            <p>
              The billing history table shows:
        </p>
        <ul>
              <li><strong>Invoice ID:</strong> Unique identifier for each invoice</li>
              <li><strong>Date:</strong> Billing cycle date</li>
              <li><strong>Amount:</strong> Total amount charged</li>
              <li><strong>Status:</strong> Payment status (paid, pending, failed)</li>
              <li><strong>Action:</strong> Download invoice button</li>
        </ul>
        <p>
              You can filter invoices by status (all, paid, pending, failed) and date range, or search by invoice ID.
        </p>
      </section>

          <section id="billing-charges" className="billing-section">
            <h2>Understand Your Invoice Charges</h2>
        <p>
              The prices of provisioned services are mostly fixed. If you provision resources (Apps, Databases, VPS instances) and leave 
              them running all month, we'll charge you a predictable amount for those resources.
            </p>
            <p>
              Common reasons for charges include:
        </p>
            <ul>
              <li><strong>Running Resources:</strong> Apps, databases, and VPS instances incur charges while running</li>
              <li><strong>Storage:</strong> Database storage and cloud storage usage</li>
              <li><strong>Compute:</strong> CPU and memory allocation for your applications</li>
              <li><strong>Network:</strong> Data transfer and bandwidth usage</li>
            </ul>
            <p>
              For a breakdown of charges, check out your invoice in the Billing section of the <strong>dashboard</strong>. Each invoice 
              includes detailed line items showing what you were charged for and when.
            </p>
            <p>
              We're happy to discuss a refund if you do create compute resources by mistake, or if your app receives unexpected traffic 
              due to an attack and generates a surprisingly large bill. Just send an email to <a href="mailto:support@aedify.ai" className="inline-link">support@aedify.ai</a>.
            </p>
      </section>

          <section id="billing-stripe" className="billing-section">
            <h2>Manage in Stripe</h2>
        <p>
              For advanced billing management, you can access the Stripe Customer Portal directly from the Billing section. Click the 
              <strong> "Manage in Stripe"</strong> button to:
        </p>
        <ul>
              <li>View detailed billing history</li>
              <li>Update payment methods</li>
              <li>Download invoices and receipts</li>
              <li>Update billing address</li>
              <li>Manage subscriptions (if applicable)</li>
        </ul>
            <p>
              The Stripe portal opens in a new window and provides full access to your billing account.
            </p>
      </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Section Navigation */}
        <aside className="billing-sidebar">
          <div className="sidebar-section">
            <h3>Contents</h3>
            <nav className="section-nav">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#billing-${section.id}`}
                  className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveSection(section.id)
                    document.getElementById(`billing-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default Billing
